/**
 * Build stamp shared by analytics (PostHog super property `app_version`,
 * onboarding v3 events, journey_timing) and error reporting (Sentry release).
 *
 * - Native: the store binary's marketing version plus build number, read from
 *   Capacitor App.getInfo(): "3.14.25 (412)". `appBuild` is the build number.
 * - Web: "web-<first 7 of the commit sha>" when Vercel exposes the sha to the
 *   build (VITE_VERCEL_GIT_COMMIT_SHA), else "web-<vite mode>".
 *
 * Every reader must cope with a missing value: a failed native lookup
 * resolves to 'unknown', never throws, and callers that cannot wait use the
 * synchronous peek.
 */
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

export interface AppVersionInfo {
  /** Same string onboarding v3 events have always carried as app_version. */
  appVersion: string;
  /** Native build number, null on web or when the store did not report one. */
  appBuild: string | null;
}

export const UNKNOWN_APP_VERSION = 'unknown';

let infoPromise: Promise<AppVersionInfo> | null = null;
let resolvedInfo: AppVersionInfo | null = null;

function isNativeRuntime(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** Synchronous web build id. Also what a native webview bundle was built as. */
export function getWebBuildId(): string {
  const raw = import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA as string | undefined;
  const sha = typeof raw === 'string' ? raw.trim() : '';
  return sha ? `web-${sha.slice(0, 7)}` : `web-${import.meta.env.MODE}`;
}

function cleanPart(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function nativeInfo(version: unknown, build: unknown): AppVersionInfo {
  const cleanVersion = cleanPart(version);
  const cleanBuild = cleanPart(build);
  if (!cleanVersion) return { appVersion: UNKNOWN_APP_VERSION, appBuild: cleanBuild };
  return {
    appVersion: cleanBuild ? `${cleanVersion} (${cleanBuild})` : cleanVersion,
    appBuild: cleanBuild,
  };
}

/** Resolves once per page load. Never rejects. */
export function getAppVersionInfo(): Promise<AppVersionInfo> {
  if (infoPromise) return infoPromise;
  if (!isNativeRuntime()) {
    resolvedInfo = { appVersion: getWebBuildId(), appBuild: null };
    infoPromise = Promise.resolve(resolvedInfo);
    return infoPromise;
  }
  infoPromise = Promise.resolve()
    .then(() => CapacitorApp.getInfo())
    .then((info) => nativeInfo(info?.version, info?.build))
    .catch(() => ({ appVersion: UNKNOWN_APP_VERSION, appBuild: null }))
    .then((info) => {
      resolvedInfo = info;
      return info;
    });
  return infoPromise;
}

export function getAppVersion(): Promise<string> {
  return getAppVersionInfo().then((info) => info.appVersion);
}

/**
 * The version if it is already known, without waiting. Web always knows it;
 * native knows it after getAppVersionInfo() has resolved once.
 */
export function peekAppVersionInfo(): AppVersionInfo | null {
  if (resolvedInfo) return resolvedInfo;
  if (!isNativeRuntime()) {
    resolvedInfo = { appVersion: getWebBuildId(), appBuild: null };
    return resolvedInfo;
  }
  return null;
}

/**
 * Waits at most `timeoutMs` for the version. Used where a slow native bridge
 * must not hold up startup (Sentry init); resolves null on timeout.
 */
export function resolveAppVersionInfo(timeoutMs: number): Promise<AppVersionInfo | null> {
  const known = peekAppVersionInfo();
  if (known) return Promise.resolve(known);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), Math.max(0, timeoutMs));
    void getAppVersionInfo().then((info) => {
      clearTimeout(timer);
      resolve(info);
    });
  });
}

/** Test-only: forget the cached version. */
export function __resetAppVersionForTests(): void {
  infoPromise = null;
  resolvedInfo = null;
}
